import type { ReactNode } from 'react'
import type { ApiKey } from '../types/api'
import type { CreatorLabel } from '../lib/creator'
import { secretLabel, type KeyChain } from '../lib/keyChains'
import type { LastUsedLabel } from '../lib/lastUsed'
import { describeWarning, type OldSecretWarning } from '../lib/oldSecretWarning'
import KeyBadges from './KeyBadges'
import LastUsedValue from './LastUsedValue'
import SecretList from './SecretList'
import Timestamp from './Timestamp'

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-gray-400 text-xs uppercase tracking-wide">{label}</dt>
      <dd className="text-gray-900 mt-0.5">{children}</dd>
    </div>
  )
}

interface KeyChainCardProps {
  chain: KeyChain
  creator: CreatorLabel
  lastUsed: LastUsedLabel | null
  warnings: OldSecretWarning[]
  usageTrackedSince: string | null
  now: Date
  isDeleting?: boolean
  deleteError?: string
  isNew?: boolean
  onDeleteChain?: (chain: KeyChain) => void
  onDeleteSecret?: (secret: ApiKey) => void
  onClearError?: (chain: KeyChain) => void
  onShowUsage?: (clientId: string, keyName: string) => void
}

export default function KeyChainCard({
  chain,
  creator,
  lastUsed,
  warnings,
  usageTrackedSince,
  now,
  isDeleting = false,
  deleteError,
  isNew = false,
  onDeleteChain,
  onDeleteSecret,
  onClearError,
  onShowUsage,
}: KeyChainCardProps) {
  const apiKey = chain.current
  const warned = new Set(warnings.map((w) => w.old.id))
  // Build the className based on state
  const getCardClassName = () => {
    const baseClasses = 'bg-white rounded-xl shadow-card border border-gray-100 overflow-hidden transition-all duration-300 relative h-full flex flex-col hover:shadow-elevated'
    
    if (deleteError) {
      return `${baseClasses} ring-2 ring-red-500`
    }
    if (isNew) {
      return `${baseClasses} animate-pulse ring-2 ring-accent ring-opacity-50`
    }
    return `${baseClasses} opacity-100 scale-100`
  }

  return (
    <div className={getCardClassName()}>
      {/* Loading overlay when deleting */}
      {isDeleting && (
        <div className="absolute inset-0 bg-gray-900/50 flex items-center justify-center z-10 rounded-lg">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-white border-t-transparent"></div>
            <span className="mt-2 text-sm font-medium text-white">Deleting...</span>
          </div>
        </div>
      )}

      {/* Error overlay */}
      {deleteError && (
        <div className="absolute inset-0 bg-red-900/90 flex items-center justify-center z-10 rounded-lg p-4">
          <div className="flex flex-col items-center text-center">
            <svg className="h-8 w-8 text-red-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="mt-2 text-sm font-medium text-white">Delete failed</p>
            <p className="mt-1 text-xs text-red-200">{deleteError}</p>
            <button
              onClick={() => onClearError?.(chain)}
              className="mt-3 px-3 py-1.5 text-xs font-medium text-red-900 bg-white rounded-md hover:bg-red-50 transition-colors"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div className="px-4 py-5 sm:p-6 flex flex-col h-full">
        {/* Header: Name and Status. The badges move below the name rather than leave it less than 10rem. */}
        <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
          <h3 className="text-lg font-medium text-gray-900 truncate flex-1 basis-40 min-w-0" title={apiKey.display_name}>
            {apiKey.display_name}
          </h3>
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            <KeyBadges chain={chain} warnings={warnings} />
          </div>
        </div>

        {/* Description - fixed height area for consistency */}
        <div className="mt-2 h-10">
          {apiKey.description && (
            <p className="text-sm text-gray-500 line-clamp-2" title={apiKey.description}>
              {apiKey.description}
            </p>
          )}
        </div>

        {/* Fixed cells, so every fact sits in the same place on every card. Tracking is
            on or off for the whole page, so all cards have the same cells. */}
        <dl className="mt-4 grid grid-cols-[3fr_2fr] gap-x-6 gap-y-3 text-sm">
          {lastUsed && (
            <Fact label="Last used">
              <LastUsedValue label={lastUsed} />
            </Fact>
          )}
          <Fact label="Expires"><Timestamp value={apiKey.expires_at} /></Fact>
          <Fact label="Created"><Timestamp value={chain.createdAt} /></Fact>
          <Fact label="Secret">
            <span className="font-mono">{apiKey.hint ? `${apiKey.hint}…` : '—'}</span>
          </Fact>
          <Fact label="Created by">
            <span className={creator.text === 'Not recorded' ? 'text-gray-500' : ''} title={creator.title}>
              {creator.text}
            </span>
          </Fact>
          <Fact label="Last rotated">{chain.lastRotatedAt ? <Timestamp value={chain.lastRotatedAt} /> : '—'}</Fact>
        </dl>

        {chain.olderWorking
          .filter((s) => !warned.has(s.id))
          .map((s) => (
            <p key={s.id} className="mt-3 text-xs text-gray-500">
              Previous secret <span className="font-mono">{secretLabel(s)}</span> works until <Timestamp value={s.expires_at} />
            </p>
          ))}

        {warnings.map((warning) => {
          const { title, body } = describeWarning(warning)
          return (
            <div key={warning.old.id} className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs font-medium text-amber-900">{title}</p>
              <p className="mt-1 text-xs text-amber-800">{body}</p>
            </div>
          )
        })}

        {chain.secrets.length > 1 && (
          <SecretList chain={chain} usageTrackedSince={usageTrackedSince} now={now} onDeleteSecret={onDeleteSecret} />
        )}

        {/* Spacer: only the footer is pinned to the bottom */}
        <div className="flex-1"></div>

        {/* Action buttons */}
        <div className="mt-4 pt-3 border-t border-gray-100 flex justify-between items-center">
          <button
            onClick={() => onShowUsage?.(apiKey.client_id, apiKey.display_name)}
            className="text-sm font-medium text-accent hover:text-accent-dark inline-flex items-center gap-1 transition-colors"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            How to Use
          </button>
          {onDeleteChain && (
            <button
              onClick={() => onDeleteChain(chain)}
              disabled={isDeleting || !!deleteError}
              className="text-sm font-medium text-red-600 hover:text-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Delete
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
